import type { IncidentPower, PowerSummary } from './power';
// kmgr 回的原始形狀（cloud/kmgr api/openapi.json 0.16.0）與轉成畫面形狀（types.ts）的轉換。
// 只有這一份知道 kmgr 的欄位名稱；kmgr 改欄位時改這裡與 types.ts，畫面不用動。
import type {
  AgentCounts,
  AffectedTarget,
  BusinessHours,
  CollectorOffline,
  GlobalStop,
  HomeKmgrStatus,
  HomeNetwork,
  HomeStatus,
  HostList,
  HostRow,
  Incident,
  IncidentCounts,
  IncidentEvent,
  IncidentList,
  KinioSummary,
  KmgrDashboard,
  Maintenance,
  NetworkSummary,
  OrgKmgrStatus,
  OrgSeats,
  PersonRef,
  Probe,
  SecurityPolicy,
  Severity,
  SiteInfo,
  SourceState,
  ThresholdRule,
  Thresholds,
} from './types';
import { defaultThresholds } from './model';

/** 顯示名稱（kmgr 經 app-api 查；查不到的 id 不在裡面）。 */
export interface WireNames {
  homes?: Record<string, string>;
  users?: Record<string, string>;
  orgs?: Record<string, string>;
  things?: Record<string, string>;
}

export interface WireIncident {
  incident_id: string;
  org_id: string;
  home_id: string;
  kind: string;
  severity: 'p1' | 'p2' | 'p3';
  now_state: 'ongoing' | 'recovered';
  handling: 'open' | 'acknowledged' | 'claimed' | 'resolved';
  claimed_by?: string;
  claimed_at?: string;
  acknowledged_by?: string;
  acknowledged_at?: string;
  /** user_id；system = P2 / P3 已恢復 24 小時沒人處理的自動結案 */
  resolved_by?: string;
  resolved_at?: string;
  version: number;
  recurrence_count?: number;
  recurring?: boolean;
  target_ref?: string;
  subject?: string;
  affected?: number;
  opened_at: string;
  recovered_at?: string;
  silenced_until?: string;
  escalation_step?: number;
  external_ticket?: string;
  updated_at?: string;
}

export interface WireIncidentList {
  incidents: WireIncident[];
  next_cursor?: string;
  can_handle?: boolean;
  scope?: string;
  names?: WireNames;
}

export interface WireAlert {
  alert_id: string;
  target_ref: string;
  kind: string;
  subject?: string;
  severity: string;
  opened_at: string;
  resolved_at?: string;
}

export interface WireHistory {
  version: number;
  from?: string;
  handling: Incident['handling'];
  now_state: Incident['now_state'];
  cause: 'opened' | 'recurred' | 'recovered' | 'user' | 'auto_resolved';
  by: string;
  assignee?: string;
  note?: string;
  via_delegation_id?: string;
  at: string;
}

export interface WireNotification {
  key: 'kmgr_alert.opened' | 'kmgr_alert.resolved';
  variant: string;
  route: string;
  schedule_id?: string;
  user_ids?: string[];
  roles?: string[];
  priority: string;
  at: string;
}

export interface WireIncidentDetail {
  incident: WireIncident;
  /** 停電事故的影響區（kmgr incidents.go powerOfIncident） */
  power?: IncidentPower;
  alerts?: WireAlert[];
  history?: WireHistory[];
  notifications?: WireNotification[];
  can_handle?: boolean;
  names?: WireNames;
}

export interface WireStateResponse {
  incident: WireIncident;
  changed: boolean;
}

export interface WireCollectorOffline {
  device_ref: string;
  last_seen_at?: string;
}

export interface WireHomeNetwork {
  source_state: 'unset' | 'ok' | 'unreadable';
  reason?: 'collector_offline' | 'snmp_auth_failed';
  collectors?: number;
  collectors_offline?: WireCollectorOffline[];
  probes?: number;
  probes_up?: number;
  probes_failing?: number;
  probes_unassigned?: number;
  probes_no_result?: number;
}

export interface WireHomeStatus {
  home_id: string;
  org_id: string;
  state: HomeStatus['state'];
  agents: AgentCounts;
  incidents: IncidentCounts;
  last_seen_at?: string;
  network?: WireHomeNetwork;
  maintenance?: { until?: string; set_by?: string };
  after_hours?: boolean;
}

export interface WireNetworkSummary {
  source_state: 'unset' | 'ok' | 'unreadable';
  unreadable_homes?: number;
  collectors?: number;
  collectors_offline?: WireCollectorOffline[];
  probes?: number;
  probes_up?: number;
  probes_failing?: number;
}

export type WireKinio = Partial<KinioSummary> & { source_state: SourceState };

/** SPEC #kinio-kms-global-stop：kmgr 讀得到時 dashboard 與 GET /v1/orgs/{org}/kmgr 回 global_stop {active, scope, started_at, reason}。 */
export interface WireGlobalStop {
  active: boolean;
  scope?: 'all' | 'org';
  started_at?: string;
  reason?: string;
}

export interface WireDashboard {
  scope: string;
  checked_at: string;
  incidents: { source_state: SourceState; counts: IncidentCounts; items: WireIncident[] };
  homes: { source_state: SourceState; total: number; counts: Partial<Record<HomeStatus['state'], number>>; items: WireHomeStatus[]; others_ok: number };
  hosts: AgentCounts & { source_state: SourceState; disk_full?: number };
  network?: WireNetworkSummary;
  kinio?: WireKinio;
  global_stop?: WireGlobalStop;
  names?: WireNames;
  /** M5-04：範圍含 2 個以上組織時才有 */
  by_org?: WireOrgSummary[];
  /** 能源 NRG-K5（kmgr power.Summary） */
  power?: PowerSummary;
}

export interface WireOrgSummary {
  org_id: string;
  homes?: number;
  incidents_open?: number;
  p1_open?: number;
  p1_unclaimed?: number;
  unclaimed?: number;
  homes_by_state?: Partial<Record<HomeStatus['state'], number>>;
  hosts_offline?: number;
  hosts_disk_full?: number;
  network?: { source_state?: 'unset' | 'ok' | 'unreadable'; unreadable_homes?: number; collectors_offline?: number; probes_failing?: number };
  ups_on_battery?: number;
  ups_min_runtime_s?: number;
  poe_over_warn?: number;
}

export interface WireOrgKmgr {
  org_id: string;
  dpa: { current_version: string; summary?: string[]; full_text_url?: string; accepted: boolean; accepted_version?: string; accepted_by?: string; accepted_at?: string };
  homes: { home_id: string; kmgr_enabled: boolean }[];
  stopped_at?: string;
  global_stop?: WireGlobalStop;
  security_policy?: SecurityPolicy;
  names?: WireNames;
}

export type WireHomeKmgr = WireHomeStatus & { kmgr_enabled: boolean; dpa_accepted: boolean; can_configure: boolean; can_handle: boolean; can_remote?: boolean };

/** GET /v1/me/kmgr/hosts 與 targets（kind = agent）的一台（openapi Target）。 */
export interface WireHost {
  target_ref: string;
  kind?: string;
  org_id?: string;
  home_id?: string;
  critical?: boolean;
  seat_class?: HostRow['seat_class'];
  license_state?: HostRow['license_state'];
  license_reason?: HostRow['license_reason'];
  seat_id?: string;
  presence?: HostRow['presence'];
  power_state?: HostRow['power_state'];
  last_seen_at?: string;
  hostname?: string;
  os?: HostRow['os'];
  unexpected?: boolean;
  state?: HostRow['state'];
  collector?: HostRow['collector'];
  expected_offline?: HostRow['expected_offline'];
  probe?: WireProbe;
  primary_user?: string;
  primary_user_id?: string;
  metrics?: HostRow['metrics'];
  agent_version?: string;
  key_protection?: HostRow['key_protection'];
  local_paused?: HostRow['local_paused'];
  /** 遠端協助（app-api things/hosts 有，kmgr 主機清單還不帶）：允許範圍、中國大陸、主控台使用者是不是管理員 */
  remote_mode?: HostRow['remote_mode'];
  cn_mainland?: boolean;
  console_user_admin?: boolean;
  can_remote?: boolean;
}

export interface WireHostList {
  scope?: string;
  counts?: Record<string, number>;
  hosts?: WireHost[];
  names?: WireNames;
  next_cursor?: string;
}

export interface WireTargetList {
  targets?: WireHost[];
  names?: WireNames;
  next_cursor?: string;
}

export type WireProbe = Omit<Probe, 'collector_name'>;

export interface WireMaintenance {
  home_id: string;
  until?: string;
  set_by?: string;
  set_at?: string;
  schedule?: Maintenance['schedule'];
  active?: boolean;
  active_until?: string;
  summary?: string;
  changed?: boolean;
  notified?: number;
  names?: WireNames;
}

export interface WireBusinessHours {
  home_id: string;
  tz: string;
  windows?: BusinessHours['windows'];
  summary?: string;
  after_hours?: boolean;
  updated_by?: string;
  updated_at?: string;
  changed?: boolean;
  names?: WireNames;
}

/** GET·PUT …/severity-overrides（kmgr M2-13b）：updated_by 是 user_id，名稱在 names。 */
export interface WireThresholds {
  home_id: string;
  rules?: (ThresholdRule & { default_severity?: Severity; default_debounce_min?: number | null })[];
  disk_pct?: number;
  tls_days?: number;
  workstation_offline_days?: number;
  updated_by?: string;
  updated_at?: string;
  changed?: boolean;
  names?: WireNames;
}

/** GET·PUT …/site-info（kmgr M2-13b）：沒填的欄位不回。 */
export type WireSiteInfo = Omit<SiteInfo, 'updated_by'> & { updated_by?: string; changed?: boolean; names?: WireNames };

export interface WireOrgSeats {
  org_id: string;
  summary?: OrgSeats['summary'];
  seats?: OrgSeats['seats'];
  unlicensed?: Omit<OrgSeats['unlicensed'][number], 'home_name'>[];
  can_buy?: boolean;
  names?: WireNames;
}

/** 畫面自己知道的名稱（範圍切換器的家戶與組織；kmgr 的 names 查不到時用）。 */
export interface LocalNames {
  homes?: Record<string, string>;
  orgs?: Record<string, string>;
}

const SEV: Record<string, Severity> = { p1: 'P1', p2: 'P2', p3: 'P3' };

export const EMPTY_COUNTS: IncidentCounts = { total: 0, p1: 0, p2: 0, p3: 0, ongoing: 0, unclaimed: 0, p1_unclaimed: 0, mine: 0 };
export const EMPTY_AGENTS: AgentCounts = { total: 0, online: 0, offline: 0, expected_offline: 0, unlicensed: 0 };

function person(id: string | undefined, n: WireNames | undefined): PersonRef | undefined {
  if (!id) return undefined;
  const name = n?.users?.[id];
  return name ? { user_id: id, display_name: name } : { user_id: id };
}

/** 一件事故（清單、總覽、處理回應共用）。 */
export function adaptIncident(w: WireIncident, n?: WireNames, local?: LocalNames): Incident {
  const out: Incident = {
    incident_id: w.incident_id,
    home_id: w.home_id,
    org_id: w.org_id,
    org_name: n?.orgs?.[w.org_id] ?? local?.orgs?.[w.org_id],
    home_name: n?.homes?.[w.home_id] ?? local?.homes?.[w.home_id],
    kind: w.kind,
    severity: SEV[w.severity] ?? 'P3',
    target_ref: w.target_ref,
    target_name: w.target_ref ? n?.things?.[w.target_ref] : undefined,
    subject: w.subject || undefined,
    now_state: w.now_state,
    opened_at: w.opened_at,
    recovered_at: w.recovered_at,
    handling: w.handling,
    claimed_by: person(w.claimed_by, n),
    claimed_at: w.claimed_at,
    acknowledged_by: w.acknowledged_by ? [{ ...person(w.acknowledged_by, n)!, at: w.acknowledged_at }] : undefined,
    resolved_by: w.resolved_by === 'system' ? 'system' : person(w.resolved_by, n),
    resolved_at: w.resolved_at,
    version: w.version,
    recurrence_count: w.recurrence_count,
    recurring: w.recurring,
    silenced_until: w.silenced_until,
    affected_count: w.affected,
    external_ticket: w.external_ticket,
    people: { ...(n?.users ?? {}) },
  };
  return out;
}

export function adaptIncidentList(w: WireIncidentList, local?: LocalNames): IncidentList {
  return {
    incidents: (w.incidents ?? []).map((i) => ({ ...adaptIncident(i, w.names, local), can_handle: w.can_handle })),
    next_cursor: w.next_cursor || undefined,
  };
}

const ALERT_STATE: Record<string, string> = { host_offline: 'offline', service_stopped: 'service_stopped', power_outage: 'on_battery', ups_output_off: 'offline' };

/** 事故詳細：告警 → 受影響設備（一個對象 + 原因一列）、處理歷程 + 送過的通知 → 時間軸。 */
export function adaptIncidentDetail(w: WireIncidentDetail, local?: LocalNames): Incident {
  const n = w.names;
  const inc = adaptIncident(w.incident, n, local);
  inc.can_handle = w.can_handle;
  if (w.power) inc.power = w.power;
  inc.affected = (w.alerts ?? []).map(
    (a): AffectedTarget => ({
      target_ref: a.target_ref,
      // 網路設備 / UPS 探針（kpr-…）不是主機；停電事故的 UPS 名稱在 power.ups
      kind: a.target_ref.startsWith('kpr-') ? 'network_device' : 'host',
      name: n?.things?.[a.target_ref] ?? (w.power?.ups?.probe_id === a.target_ref ? (w.power.ups.name ?? '') : ''),
      state: a.resolved_at ? 'recovered' : (ALERT_STATE[a.kind] ?? 'offline'),
      note: a.subject || undefined,
    }),
  );
  const events: IncidentEvent[] = [];
  for (const h of w.history ?? []) {
    switch (h.cause) {
      case 'opened':
      case 'recurred':
        events.push({ at: h.at, kind: 'bad', code: h.cause });
        break;
      case 'recovered':
        events.push({ at: h.at, kind: 'recover', code: 'recovered' });
        break;
      case 'auto_resolved':
        events.push({ at: h.at, kind: 'system', code: 'auto_resolved' });
        break;
      default:
        events.push({ at: h.at, kind: 'person', actor: person(h.by, n), handling: h.handling, assignee: h.assignee && h.assignee !== h.by ? person(h.assignee, n) : undefined, note: h.note || undefined });
    }
  }
  for (const x of w.notifications ?? []) {
    events.push({
      at: x.at,
      kind: 'notify',
      code: x.key === 'kmgr_alert.resolved' ? 'notify_resolved' : 'notify_opened',
      recipients: (x.user_ids ?? []).map((u) => n?.users?.[u] ?? '').filter(Boolean),
      roles: x.user_ids?.length ? undefined : x.roles,
    });
  }
  inc.events = events.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  return inc;
}

/** 處理回應：只有事故本身（沒有 names）；呼叫端把它疊在畫面上那一份上。 */
export function adaptStateResponse(w: WireStateResponse): Incident {
  return adaptIncident(w.incident);
}

function collectorsOffline(list: WireCollectorOffline[] | undefined, n?: WireNames): CollectorOffline[] {
  return (list ?? []).map((c) => ({ device_ref: c.device_ref, name: n?.things?.[c.device_ref], last_seen_at: c.last_seen_at }));
}

export function adaptHomeNetwork(w: WireHomeNetwork | undefined, n?: WireNames): HomeNetwork | undefined {
  if (!w) return undefined;
  return {
    source_state: w.source_state,
    reason: w.reason,
    collectors: w.collectors ?? 0,
    collectors_offline: collectorsOffline(w.collectors_offline, n),
    probes: w.probes ?? 0,
    probes_up: w.probes_up ?? 0,
    probes_failing: w.probes_failing ?? 0,
    probes_unassigned: w.probes_unassigned,
    probes_no_result: w.probes_no_result,
  };
}

export function adaptHomeStatus(w: WireHomeStatus, n?: WireNames, local?: LocalNames): HomeStatus {
  return {
    home_id: w.home_id,
    org_id: w.org_id,
    org_name: n?.orgs?.[w.org_id] ?? local?.orgs?.[w.org_id],
    home_name: n?.homes?.[w.home_id] ?? local?.homes?.[w.home_id] ?? '',
    state: w.state,
    agents: w.agents ?? EMPTY_AGENTS,
    incidents: w.incidents ?? EMPTY_COUNTS,
    last_seen_at: w.last_seen_at,
    maintenance_until: w.maintenance?.until,
    maintenance_by: person(w.maintenance?.set_by, n),
    off_hours: w.after_hours || undefined,
    network: adaptHomeNetwork(w.network, n),
  };
}

export function adaptGlobalStop(w: WireGlobalStop | undefined): GlobalStop | undefined {
  if (!w) return undefined;
  return { active: !!w.active, scope: w.scope, started_at: w.started_at, reason: w.reason };
}

function adaptNetworkSummary(w: WireNetworkSummary | undefined, n?: WireNames): NetworkSummary | undefined {
  if (!w) return undefined;
  return {
    source_state: w.source_state,
    unreadable_homes: w.unreadable_homes ?? 0,
    collectors: w.collectors ?? 0,
    collectors_offline: collectorsOffline(w.collectors_offline, n),
    probes: w.probes ?? 0,
    probes_up: w.probes_up ?? 0,
    probes_failing: w.probes_failing ?? 0,
  };
}

function adaptKinio(w: WireKinio | undefined): KinioSummary | undefined {
  if (!w) return undefined;
  const z = (v: number | undefined) => v ?? 0;
  return {
    source_state: w.source_state ?? 'ok',
    homes: z(w.homes),
    unreadable: z(w.unreadable),
    devices: z(w.devices),
    channels: z(w.channels),
    cameras: z(w.cameras),
    recorders: z(w.recorders),
    offline_devices: z(w.offline_devices),
    offline_channels: z(w.offline_channels),
    storage_faults: z(w.storage_faults),
    recording_gaps: z(w.recording_gaps),
    recording_continuous: z(w.recording_continuous),
    checked_at: w.checked_at,
  };
}

export function adaptDashboard(w: WireDashboard, local?: LocalNames): KmgrDashboard {
  const n = w.names;
  return {
    checked_at: w.checked_at,
    incidents: {
      source_state: w.incidents?.source_state ?? 'ok',
      counts: { ...EMPTY_COUNTS, ...(w.incidents?.counts ?? {}) },
      items: (w.incidents?.items ?? []).map((i) => adaptIncident(i, n, local)),
    },
    homes: {
      source_state: w.homes?.source_state ?? 'ok',
      total: w.homes?.total ?? 0,
      counts: w.homes?.counts ?? {},
      items: (w.homes?.items ?? []).map((h) => adaptHomeStatus(h, n, local)),
      others_ok: w.homes?.others_ok ?? 0,
    },
    hosts: { ...EMPTY_AGENTS, ...(w.hosts ?? { source_state: 'ok' as const }) },
    network: adaptNetworkSummary(w.network, n),
    kinio: adaptKinio(w.kinio),
    power: w.power,
    global_stop: adaptGlobalStop(w.global_stop),
    by_org: w.by_org?.map((o) => ({
      org_id: o.org_id,
      org_name: n?.orgs?.[o.org_id] ?? local?.orgs?.[o.org_id],
      homes: o.homes ?? 0,
      incidents_open: o.incidents_open ?? 0,
      p1_open: o.p1_open ?? 0,
      p1_unclaimed: o.p1_unclaimed ?? 0,
      unclaimed: o.unclaimed ?? 0,
      homes_by_state: o.homes_by_state ?? {},
      hosts_offline: o.hosts_offline ?? 0,
      hosts_disk_full: o.hosts_disk_full ?? 0,
      ups_on_battery: o.ups_on_battery,
      ups_min_runtime_s: o.ups_min_runtime_s,
      poe_over_warn: o.poe_over_warn,
      network: {
        source_state: o.network?.source_state ?? 'unset',
        unreadable_homes: o.network?.unreadable_homes ?? 0,
        collectors_offline: o.network?.collectors_offline ?? 0,
        probes_failing: o.network?.probes_failing ?? 0,
      },
    })),
  };
}

export function adaptOrgKmgr(w: WireOrgKmgr): OrgKmgrStatus {
  const by = w.dpa.accepted_by;
  return {
    org_id: w.org_id,
    dpa: {
      current_version: w.dpa.current_version,
      summary: w.dpa.summary ?? [],
      full_text_url: w.dpa.full_text_url || undefined,
      accepted: !!w.dpa.accepted,
      accepted_version: w.dpa.accepted_version || undefined,
      accepted_by: by ? { user_id: by, display_name: w.names?.users?.[by] } : undefined,
      accepted_at: w.dpa.accepted_at,
    },
    homes: w.homes ?? [],
    stopped_at: w.stopped_at,
    global_stop: adaptGlobalStop(w.global_stop),
    security_policy: w.security_policy,
  };
}

export function adaptHomeKmgr(w: WireHomeKmgr): HomeKmgrStatus {
  return {
    home_id: w.home_id,
    org_id: w.org_id,
    kmgr_enabled: !!w.kmgr_enabled,
    dpa_accepted: !!w.dpa_accepted,
    state: w.state,
    agents: w.agents ?? EMPTY_AGENTS,
    incidents: { ...EMPTY_COUNTS, ...(w.incidents ?? {}) },
    last_seen_at: w.last_seen_at,
    can_configure: !!w.can_configure,
    can_handle: !!w.can_handle,
    can_remote: !!w.can_remote,
    network: adaptHomeNetwork(w.network),
    maintenance_until: w.maintenance?.until,
    off_hours: w.after_hours || undefined,
  };
}

/** 一台主機（hosts 清單與 targets 共用）。 */
export function adaptHost(w: WireHost, n?: WireNames, local?: LocalNames, homeId?: string): HostRow {
  const home = w.home_id ?? homeId ?? '';
  return {
    target_ref: w.target_ref,
    home_id: home,
    org_id: w.org_id,
    home_name: n?.homes?.[home] ?? local?.homes?.[home],
    org_name: w.org_id ? (n?.orgs?.[w.org_id] ?? local?.orgs?.[w.org_id]) : undefined,
    hostname: w.hostname || n?.things?.[w.target_ref],
    os: w.os,
    seat_class: w.seat_class,
    critical: w.critical,
    license_state: w.license_state ?? 'licensed',
    license_reason: w.license_reason,
    seat_id: w.seat_id,
    presence: w.presence ?? 'unknown',
    power_state: w.power_state,
    last_seen_at: w.last_seen_at,
    state: w.state ?? 'active',
    unexpected: w.unexpected,
    collector: w.collector,
    expected_offline: w.expected_offline,
    primary_user: w.primary_user,
    primary_user_id: w.primary_user_id,
    metrics: w.metrics,
    agent_version: w.agent_version,
    key_protection: w.key_protection,
    local_paused: w.local_paused,
    remote_mode: w.remote_mode,
    cn_mainland: w.cn_mainland,
    console_user_admin: w.console_user_admin,
    can_remote: w.can_remote,
  };
}

export function adaptHostList(w: WireHostList, local?: LocalNames): HostList {
  return { counts: w.counts ?? {}, hosts: (w.hosts ?? []).map((h) => adaptHost(h, w.names, local)), next_cursor: w.next_cursor || undefined };
}

/** targets?kind=agent（一個家戶）。 */
export function adaptTargetHosts(w: WireTargetList, homeId: string, local?: LocalNames): HostRow[] {
  return (w.targets ?? []).filter((t) => !t.kind || t.kind === 'agent').map((t) => adaptHost(t, w.names, local, homeId));
}

/** targets?kind=probe：target_ref = probe_id、內容在 probe。 */
export function adaptProbes(w: WireTargetList, homeId: string): Probe[] {
  return (w.targets ?? [])
    .map((t) => t.probe)
    .filter((p): p is WireProbe => !!p)
    .map((p) => ({ ...p, home_id: p.home_id || homeId, collector_name: p.collector ? w.names?.things?.[p.collector] : undefined }));
}

export function adaptMaintenance(w: WireMaintenance): Maintenance {
  return {
    home_id: w.home_id,
    until: w.until,
    set_by: person(w.set_by, w.names),
    set_at: w.set_at,
    schedule: w.schedule?.windows?.length ? w.schedule : undefined,
    active: !!w.active,
    active_until: w.active_until,
    summary: w.summary,
    changed: w.changed,
    notified: w.notified,
  };
}

export function adaptBusinessHours(w: WireBusinessHours): BusinessHours {
  return { home_id: w.home_id, tz: w.tz, windows: w.windows ?? [], summary: w.summary, after_hours: w.after_hours, updated_by: person(w.updated_by, w.names), updated_at: w.updated_at, changed: w.changed };
}

/** 門檻與等級：沒回的數字用預設；列照 kmgr 的（kmgr 一律回十列，順序同預設表）。 */
export function adaptThresholds(w: WireThresholds): Thresholds {
  const def = defaultThresholds(w.home_id);
  return {
    home_id: w.home_id,
    rules: (w.rules ?? def.rules).map((r) => ({ key: r.key, severity: r.severity, debounce_min: r.debounce_min ?? null, overridden: !!r.overridden })),
    disk_pct: w.disk_pct ?? def.disk_pct,
    tls_days: w.tls_days ?? def.tls_days,
    workstation_offline_days: w.workstation_offline_days ?? def.workstation_offline_days,
    updated_by: person(w.updated_by, w.names),
    updated_at: w.updated_at,
  };
}

export function adaptSiteInfo(w: WireSiteInfo): SiteInfo {
  const { names, changed: _c, updated_by, ...rest } = w;
  return { ...rest, updated_by: person(updated_by, names) };
}

export function adaptOrgSeats(w: WireOrgSeats, local?: LocalNames): OrgSeats {
  return {
    org_id: w.org_id,
    summary: w.summary ?? [],
    seats: (w.seats ?? []).map((s) => ({ ...s, hostname: s.hostname || (s.assigned_ref ? w.names?.things?.[s.assigned_ref] : undefined) })),
    unlicensed: (w.unlicensed ?? []).map((u) => ({ ...u, hostname: u.hostname || w.names?.things?.[u.device_ref], home_name: w.names?.homes?.[u.home_id] ?? local?.homes?.[u.home_id] })),
    can_buy: !!w.can_buy,
  };
}
